import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Save, FolderOpen, Trash2, ChevronDown, Loader2 } from "lucide-react";

/**
 * Reusable "save/load poster template" bar for Competition, Test Timetable,
 * Birthday etc. Persists a serialisable snapshot of the editor state to the
 * backend so teachers can re-apply their favourite layouts in one tap.
 *
 * Props:
 *   kind       — poster kind key (competition | test-timetable | birthday …)
 *   getState   — () => plain-JSON snapshot to save
 *   applyState — (snapshot) => void, restore editor state
 *   testIdPrefix — optional prefix for data-testid attributes (default: kind)
 */
export default function TemplateBar({ kind, getState, applyState, testIdPrefix }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const loadedOnce = useRef(false);
  const pfx = testIdPrefix || kind;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/poster-templates?kind=${encodeURIComponent(kind)}`);
      setItems(Array.isArray(data) ? data : []);
    } catch { /* first load — silent */ }
    finally { setLoading(false); loadedOnce.current = true; }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const doSave = async () => {
    const name = window.prompt("Template name (e.g. \"Half-Yearly A&B\")");
    if (!name || !name.trim()) return;
    setSaving(true);
    try {
      const snap = getState() || {};
      await api.post("/poster-templates", { kind, name: name.trim(), data: snap });
      toast.success(`Saved as \"${name.trim()}\".`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the template.");
    } finally { setSaving(false); }
  };

  const doLoad = (tpl) => {
    if (!tpl) return;
    if (!window.confirm(`Load \"${tpl.name}\"? Any unsaved edits will be replaced.`)) return;
    try {
      applyState(tpl.data || {});
      toast.success(`Loaded \"${tpl.name}\".`);
    } catch {
      toast.error("This template couldn't be applied.");
    }
  };

  const doDelete = async (tpl) => {
    if (!window.confirm(`Delete \"${tpl.name}\"? This cannot be undone.`)) return;
    try {
      await api.delete(`/poster-templates/${tpl.id}`);
      toast.success("Template deleted.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete the template.");
    }
  };

  return (
    <div className="flex items-center gap-2" data-testid={`${pfx}-template-bar`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="rounded-full" data-testid={`${pfx}-tpl-load`}>
            {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FolderOpen className="h-4 w-4 mr-1.5" />}
            Load template
            <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-70" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72 max-h-80 overflow-y-auto">
          <DropdownMenuLabel className="text-xs uppercase tracking-wide text-slate-500">Saved templates</DropdownMenuLabel>
          {items.length === 0 ? (
            <div className="px-3 py-4 text-xs text-slate-500" data-testid={`${pfx}-tpl-empty`}>
              None yet — save your first one to reuse it later.
            </div>
          ) : items.map((t) => (
            <div key={t.id} className="flex items-center gap-1 pr-1" data-testid={`${pfx}-tpl-item-${t.id}`}>
              <DropdownMenuItem className="flex-1 flex-col items-start gap-0.5" onClick={() => doLoad(t)}>
                <div className="text-sm font-medium">{t.name}</div>
                <div className="text-[11px] text-slate-500">by {t.owner_name || "Unknown"}</div>
              </DropdownMenuItem>
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); doDelete(t); }}
                className="h-7 w-7 rounded-md text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 flex items-center justify-center"
                title="Delete template"
                data-testid={`${pfx}-tpl-del-${t.id}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={doSave} data-testid={`${pfx}-tpl-save-from-menu`}>
            <Save className="h-4 w-4 mr-1.5" /> Save current as template…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button size="sm" className="rounded-full" onClick={doSave} disabled={saving} data-testid={`${pfx}-tpl-save`}>
        {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
        Save as template
      </Button>
    </div>
  );
}
